import React, { useRef, useState, useEffect } from 'react';
import { CloudSun, CloudRain, Sun, Wind, Droplets, Sunrise, Sunset, Leaf, CalendarDays, Activity } from 'lucide-react';
import { getWeatherIcon } from './index';

const WeatherModal = ({ globalWeatherData, isMaximized = false }) => {
  const currentTemp = globalWeatherData?.now?.temp || '--';
  const weatherText = globalWeatherData?.now?.text || '暂无数据';
  const locationCity = globalWeatherData?.location?.adm2 || '';
  const locationName = globalWeatherData?.location?.name || '';
  const windDir = globalWeatherData?.now?.windDir || '--';
  const windScale = globalWeatherData?.now?.windScale || '--';
  const precip = globalWeatherData?.now?.precip || '0';
  const humidity = globalWeatherData?.now?.humidity || '--';
  const pressure = globalWeatherData?.now?.pressure || '--';
  
  const airNow = globalWeatherData?.air || {};
  const aqiCategory = airNow.category || '优';
  const aqiValue = airNow.aqi || '';
  const aqiText = `${aqiCategory} ${aqiValue}`.trim();

  const tz = globalWeatherData?.location?.tz || 'Asia/Shanghai';
  let tzName = '';
  try {
    const parts = new Intl.DateTimeFormat('zh-CN', { timeZone: tz, timeZoneName: 'long' }).formatToParts(new Date());
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    if (tzPart) {
      tzName = tzPart.value.replace('中国标准时间', '北京时间');
    }
  } catch(e) {
    console.error(e);
  }

  const allDaily = globalWeatherData?.daily || [];
  const dailyForecasts = isMaximized ? allDaily.slice(0, 15) : allDaily.slice(0, 7);
  const today = dailyForecasts[0] || {};
  const tempMax = today?.tempMax || '--';
  const tempMin = today?.tempMin || '--';
  const sunrise = today?.sunrise || '--:--';
  const sunset = today?.sunset || '--:--';

  const allHourly = globalWeatherData?.hourly || [];
  
  // 过滤掉已经过去的小时（解决服务端2小时缓存导致的旧时间轴）
  // 只要该小时还没有完全结束（当前时间 < 预报时间 + 1小时），就保留在列表中
  const nowTime = new Date().getTime();
  const validHourly = allHourly.filter(hour => {
    const hourTime = new Date(hour.fxTime).getTime();
    return hourTime + 60 * 60 * 1000 > nowTime;
  });

  const hourlyForecasts = isMaximized ? validHourly.slice(0, 24) : validHourly.slice(0, 12);
  const indices = globalWeatherData?.indices || [];

  const [hourlyChartWidth, setHourlyChartWidth] = useState(0);
  const hourlyContainerRef = useRef(null);

  const renderHourlyChart = () => {
    if (!hourlyForecasts.length || hourlyChartWidth === 0) return null;
    const temps = hourlyForecasts.map(h => parseInt(h.temp));
    const maxT = Math.max(...temps);
    const minT = Math.min(...temps);
    const range = Math.max(maxT - minT, 1);
    
    const svgHeight = 60;
    const itemWidth = hourlyChartWidth / hourlyForecasts.length;
    const totalWidth = hourlyChartWidth;

    const points = temps.map((t, i) => {
      const x = i * itemWidth + (itemWidth / 2);
      const y = svgHeight - 15 - ((t - minT) / range) * (svgHeight - 30);
      return { x, y, t };
    });

    const pathD = points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ');

    return (
      <div style={{ position: 'relative', height: `${svgHeight}px`, width: '100%', marginTop: '10px' }}>
        <svg width="100%" height={svgHeight} style={{ position: 'absolute', top: 0, left: 0, overflow: 'visible' }}>
          {/* Smooth line */}
          <path d={pathD} fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2" strokeLinejoin="round" />
          {/* Points & Text */}
          {points.map((p, i) => (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r="3" fill="#fff" />
              <text x={p.x} y={p.y - 10} fill="#fff" fontSize="12" textAnchor="middle" opacity="0.9">{p.t}°</text>
            </g>
          ))}
        </svg>
      </div>
    );
  };

  const [dailyChartWidth, setDailyChartWidth] = useState(0);
  const dailyContainerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (dailyContainerRef.current) {
        setDailyChartWidth(dailyContainerRef.current.offsetWidth);
      }
      if (hourlyContainerRef.current) {
        setHourlyChartWidth(hourlyContainerRef.current.offsetWidth);
      }
    };
    
    // Slight delay to ensure DOM layout has updated
    setTimeout(handleResize, 10);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dailyForecasts, hourlyForecasts, isMaximized]);

  const renderDailyChart = () => {
    if (!dailyForecasts.length || dailyChartWidth === 0) return null;
    
    const maxTemps = dailyForecasts.map(d => parseInt(d.tempMax));
    const minTemps = dailyForecasts.map(d => parseInt(d.tempMin));
    const absoluteMax = Math.max(...maxTemps);
    const absoluteMin = Math.min(...minTemps);
    const range = Math.max(absoluteMax - absoluteMin, 1);
    
    const svgHeight = 100;
    const itemWidth = dailyChartWidth / dailyForecasts.length;

    const maxPoints = maxTemps.map((t, i) => {
      const x = i * itemWidth + (itemWidth / 2);
      const y = svgHeight - 20 - ((t - absoluteMin) / range) * (svgHeight - 40);
      return { x, y, t };
    });

    const minPoints = minTemps.map((t, i) => {
      const x = i * itemWidth + (itemWidth / 2);
      const y = svgHeight - 20 - ((t - absoluteMin) / range) * (svgHeight - 40);
      return { x, y, t };
    });

    const maxPath = maxPoints.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ');
    const minPath = minPoints.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ');

    return (
      <div style={{ position: 'relative', height: `${svgHeight}px`, width: '100%', margin: '15px 0' }}>
        <svg width="100%" height={svgHeight} style={{ position: 'absolute', top: 0, left: 0, overflow: 'visible' }}>
          <path d={maxPath} fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinejoin="round" />
          <path d={minPath} fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinejoin="round" />
          
          {maxPoints.map((p, i) => (
            <g key={`max-${i}`}>
              <circle cx={p.x} cy={p.y} r="3" fill="#fff" />
              <text x={p.x} y={p.y - 10} fill="#fff" fontSize="12" textAnchor="middle" opacity="0.9">{p.t}°</text>
            </g>
          ))}
          {minPoints.map((p, i) => (
            <g key={`min-${i}`}>
              <circle cx={p.x} cy={p.y} r="3" fill="rgba(255,255,255,0.6)" />
              <text x={p.x} y={p.y + 18} fill="rgba(255,255,255,0.6)" fontSize="12" textAnchor="middle">{p.t}°</text>
            </g>
          ))}
        </svg>
      </div>
    );
  };

  const getDayName = (dateStr, i) => {
    if (i === 0) return '今天';
    if (i === 1) return '明天';
    if (i === 2) return '后天';
    const date = new Date(dateStr);
    const days = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
    return days[date.getDay()];
  };

  return (
    <div style={{ padding: '0px 40px 40px 40px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', gap: '20px', minHeight: '100%' }}>
      
      {/* 标题栏 */}
      <div style={{ fontSize: '1.4rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <CloudSun size={28} /> 天气预报
      </div>

      {/* 头部核心信息区 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <div style={{ fontSize: '1.2rem', opacity: 0.9 }}>
          {locationCity} · {locationName} {tzName ? `(${tzName})` : ''} {weatherText}
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ fontSize: '6rem', fontWeight: 600, lineHeight: 1 }}>{currentTemp}<span style={{ fontSize: '3rem', verticalAlign: 'super' }}>°</span></div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ background: 'rgba(255,255,255,0.15)', padding: '4px 12px', borderRadius: '12px', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', width: 'fit-content', backdropFilter: 'blur(10px)' }}>
              <Leaf size={14} style={{ marginRight: '6px' }} />
              空气 {aqiText}
            </div>
            <div style={{ fontSize: '1.1rem', opacity: 0.9, display: 'flex', alignItems: 'center', gap: '6px' }}>
              {getWeatherIcon(globalWeatherData?.now?.icon || '101', 20)}
              {weatherText} {windDir} {windScale}级
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '20px', fontSize: '0.95rem', opacity: 0.8, flexWrap: 'wrap' }}>
          <span>温度: {tempMin}°~{tempMax}°</span>
          <span>湿度: {humidity}%</span>
          <span>气压: {pressure}hPa</span>
          <span>降水: {precip}mm</span>
          <span>日出: {sunrise}</span>
          <span>日落: {sunset}</span>
        </div>
      </div>

      {/* 24小时逐小时预报 */}
      <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '16px', padding: '20px', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ fontSize: '0.95rem', opacity: 0.8, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CloudSun size={18} /> 24小时天气预报
        </div>
        
        <div style={{ position: 'relative' }}>
          <div ref={hourlyContainerRef} style={{ display: 'flex', position: 'relative' }}>
            {hourlyForecasts.length > 0 ? hourlyForecasts.map((hour, i) => {
              const tz = globalWeatherData?.location?.tz || 'Asia/Shanghai';
              // 提取该地区当地真实时间，将基于+0800的绝对时间点转换为当地时区的小时
              let timeStr = new Date(hour.fxTime).toLocaleTimeString('zh-CN', { timeZone: tz, hour: '2-digit', hour12: false });
              // toLocaleTimeString may return "15时" or "15" depending on browser, so clean it
              timeStr = timeStr.replace('时', '').padStart(2, '0');
              return (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: '8px', zIndex: 1, padding: '10px 0' }}>
                  <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>{timeStr}时</div>
                  {getWeatherIcon(hour.icon, 24)}
                </div>
              );
            }) : (
              <div style={{ padding: '20px', opacity: 0.5 }}>暂无逐小时数据</div>
            )}
          </div>
          {renderHourlyChart()}
        </div>
      </div>

      {/* 7天预报 */}
      <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '16px', padding: '20px', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ fontSize: '0.95rem', opacity: 0.8, marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CalendarDays size={18} /> {isMaximized ? '15日天气预报' : '7日天气预报'}
          </div>
        </div>
        
        <div ref={dailyContainerRef} style={{ display: 'flex', position: 'relative' }}>
          {dailyForecasts.length > 0 ? dailyForecasts.map((day, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, zIndex: 1, background: i === 0 ? 'rgba(255,255,255,0.08)' : 'transparent', borderRadius: '12px', padding: '10px 0' }}>
              <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>{getDayName(day.fxDate, i)}</div>
              <div style={{ fontSize: '0.8rem', opacity: 0.6, marginBottom: '10px' }}>{day.fxDate.substring(5)}</div>
              {getWeatherIcon(day.iconDay, 28)}
              <div style={{ fontSize: '0.9rem', marginTop: '6px' }}>{day.textDay}</div>
            </div>
          )) : (
             <div style={{ padding: '20px', opacity: 0.5 }}>暂无每日预报数据</div>
          )}
        </div>
        {renderDailyChart()}
        
        <div style={{ display: 'flex' }}>
          {dailyForecasts.map((day, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: '4px', opacity: 0.7 }}>
              <div style={{ fontSize: '0.85rem' }}>{day.windDirDay}</div>
              <div style={{ fontSize: '0.8rem' }}>{day.windScaleDay}级</div>
            </div>
          ))}
        </div>
      </div>

      {/* 生活指数 */}
      <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '16px', padding: '20px', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ fontSize: '0.95rem', opacity: 0.8, marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Activity size={18} /> 生活指数
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '12px' }}>
          {indices.length > 0 ? indices.map((idx, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.05)', padding: '12px', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>{idx.name}</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 500 }}>{idx.category}</div>
            </div>
          )) : (
            <div style={{ padding: '10px', opacity: 0.5 }}>暂无指数数据</div>
          )}
        </div>
      </div>

    </div>
  );
};

export default WeatherModal;
